import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { isMyposEnabled } from '@/lib/payment-config';
import { formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMemo } from 'react';
import { LoyaltyRedeemSlider } from '../components/LoyaltyRedeemSlider';
import { PriceBreakdownDialog } from '../components/PriceBreakdownDialog';
import { formatCurrency } from '../mock-data';
import { PaymentMethod, type CartPricing } from '../types';
import {
  computeConsignmentTotal,
  computeCrossSellGross,
  computeTotalToPay,
} from '../wizard-running-total';
import { useWizardStore } from '../wizard-store';
import { ContractPreviewCard } from './ContractPreviewCard';
import { CrossSellProductSearch } from './CrossSellProductSearch';
import { FinalizeChecklist } from './FinalizeChecklist';

export function SummaryStep() {
  const { t } = useTranslation();
  const {
    partner,
    cart,
    startDate,
    endDate,
    pricing: pricingRaw,
    notes,
    paymentMethod,
    setPaymentMethod,
    depositPaid,
    setDepositPaid,
    crossSellItems,
    selectedPackage,
    packageSelections,
    packageClassifiedItems,
    consignmentQuantities,
    accessoryPackage,
    accessoryQuantities,
    loyaltyRedeem,
  } = useWizardStore();

  const pricing = pricingRaw as CartPricing | null;
  // FE-E: a beváltott hűségpont post-VAT credit — a grandTotal (számla-végösszeg)
  // VÁLTOZATLAN, csak a FIZETENDŐ csökken (amount STRING a drift-guard miatt).
  const loyaltyAmount = Number(loyaltyRedeem?.amount ?? 0);

  // Package consignment totals (P0-1) — Story 105-1 null-safety: a Zustand initial state-ben
  // [] ill. new Map(), de Vitest mock-ok időnként nem adják át ezeket a mezőket.
  const consignmentItems = useMemo(
    () => (packageClassifiedItems ?? []).filter(i => i.settlementType === 'CONSIGNMENT'),
    [packageClassifiedItems]
  );
  const includedItems = useMemo(
    () => (packageClassifiedItems ?? []).filter(i => i.settlementType === 'INCLUDED'),
    [packageClassifiedItems]
  );
  const consignmentTotal = useMemo(
    () => computeConsignmentTotal(packageClassifiedItems, consignmentQuantities),
    [packageClassifiedItems, consignmentQuantities]
  );

  // Cross-sell product totals (Story Q-XSL-1)
  const {
    net: crossSellNetTotal,
    vat: crossSellVat,
    gross: crossSellGrossTotal,
  } = computeCrossSellGross(crossSellItems);

  // MOST FIZETENDŐ — a computeTotalToPay SSoT-ot használjuk (ugyanaz mint a
  // StickyFooter/TopBar), hogy ne térjen el. bug-screen #2: a korábbi inline-
  // számítás KIHAGYTA a consignmentTotal-t → a végösszeg ellentmondott a footernek.
  // Kaució csak a bérlésre (Q-XSL-1); a loyalty-credit a FIZETENDŐ-ből vonódik (post-VAT).
  const totalToPay = computeTotalToPay({
    grandTotal: pricing?.grandTotal ?? 0,
    crossSellGross: crossSellGrossTotal,
    consignmentTotal,
    depositTotal: pricing?.depositTotal ?? 0,
    loyaltyAmount,
  });

  const formatDate = (date: Date | null): string => {
    if (!date) return '-';
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('rental.wizard.summaryTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('rental.wizard.summaryDesc')}
        </p>
      </div>

      {/* v3-parity step-4: strukturált szerződés-előnézet kártya (BÉRBEADÓ/BÉRLŐ + PDF-CTA) */}
      <ContractPreviewCard />

      {/* M1.1 + S4-2: véglegesítés-előtti ellenőrző-lista. Mobilon fölül (lg:hidden),
          desktopon a jobb oszlopba kerül (lg:block lent) — v3-parity proto. */}
      <div className="lg:hidden">
        <FinalizeChecklist />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left column */}
        <div className="space-y-6">
          {/* Partner */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                  1
                </span>
                {t('rental.wizard.partnerData')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">{t('partner.name')}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{partner?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">{t('common.type')}</span>
                  <span className="text-gray-900 dark:text-white">
                    {partner?.type === 'COMPANY' ? t('partner.company') : t('partner.individual')}
                  </span>
                </div>
                {partner?.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">{t('partner.phone')}</span>
                    <span className="text-gray-900 dark:text-white">{partner.phone}</span>
                  </div>
                )}
                {partner?.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">Email</span>
                    <span className="text-gray-900 dark:text-white">{partner.email}</span>
                  </div>
                )}
                {partner?.taxNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">
                      {t('partner.taxNumber')}
                    </span>
                    <span className="text-gray-900 dark:text-white">{partner.taxNumber}</span>
                  </div>
                )}
                {partner?.isVip && (
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-300">
                      VIP Partner
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Equipment List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                  2
                </span>
                {t('rental.wizard.equipmentCount', { count: cart.length })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cart.map(item => {
                  const packageNames = (packageSelections ?? [])
                    .filter(sel => sel.equipmentId === item.equipment.id)
                    .map(sel => sel.packageName);
                  return (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {item.equipment.name}
                        {packageNames.length > 0 && (
                          <span
                            data-testid={`equipment-package-${item.equipment.id}`}
                            className="ml-1 font-normal text-gray-500 dark:text-[#ccfbf1]"
                          >
                            · {packageNames.join(', ')}
                          </span>
                        )}
                      </span>
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        &times; {item.quantity}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                  3
                </span>
                {t('rental.wizard.rentalPeriod')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">{t('rental.startDate')}</span>
                  <span className="text-gray-900 dark:text-white">{formatDate(startDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">
                    {t('rental.wizard.returnLabel')}
                  </span>
                  <span className="text-gray-900 dark:text-white">{formatDate(endDate)}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">
                    {t('rental.wizard.durationLabel')}
                  </span>
                  <span className="text-primary">
                    {t('rental.wizard.durationDays', { days: pricing?.durationDays ?? '-' })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Package: Consignment items (P0-1) */}
          {selectedPackage && consignmentItems.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs text-white">
                    B
                  </span>
                  {t('rental.consignment.deliveryNote')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {consignmentItems.map(item => {
                    const qty = consignmentQuantities.get(item.id) ?? item.quantity;
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between rounded-md bg-amber-50 dark:bg-amber-900/10 px-3 py-2"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {item.notes ?? item.id}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatHuf(item.unitPrice ?? 0)}/db
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                            &times; {qty}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatHuf((item.unitPrice ?? 0) * qty)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 p-3">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-muted-foreground">
                        {t('rental.consignment.consignmentTotal')}
                      </span>
                      <span className="text-amber-600 dark:text-amber-400">
                        {formatHuf(consignmentTotal)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('rental.consignment.consignmentNote')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Package: Included items (P0-1) */}
          {selectedPackage && includedItems.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                    M
                  </span>
                  {t('rental.packageSelect.included')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {includedItems.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between rounded-md bg-blue-50 dark:bg-blue-900/10 px-3 py-1.5"
                    >
                      <span className="text-sm text-foreground">{item.notes ?? item.id}</span>
                      <span className="text-sm text-muted-foreground">&times; {item.quantity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* K3: Accessory package items */}
          {accessoryPackage && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                    A
                  </span>
                  {t('rental.wizard.suggestedAccessories')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  {accessoryPackage.name}
                </p>
                <div className="space-y-1">
                  {accessoryPackage.items
                    .filter(item => (accessoryQuantities[item.id] ?? item.quantity) > 0)
                    .map(item => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm text-muted-foreground"
                      >
                        <span>{item.notes ?? 'Tartozék'}</span>
                        <span>{accessoryQuantities[item.id] ?? item.quantity} db</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cross-sell Products (Story Q-XSL-1) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                  +
                </span>
                {t('rental.crossSell.addProduct')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CrossSellProductSearch />
            </CardContent>
          </Card>

          {/* Notes */}
          {notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('common.notes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 dark:text-[#ccfbf1]">{notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Pricing + Payment */}
        <div className="space-y-6">
          {/* S4-2: véglegesítési-lista dedikált jobb panel (csak desktopon; mobilon fölül) */}
          <div className="hidden lg:block">
            <FinalizeChecklist />
          </div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>{t('rental.wizard.paymentSummary')}</CardTitle>
            </CardHeader>
            <CardContent>
              {pricing && (
                <div className="space-y-4">
                  {/* Rental fee section */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.crossSell.rentalFee')}
                      </span>
                      <span className="text-gray-900 dark:text-white" data-testid="summary-net">
                        {formatCurrency(pricing.subtotal)}
                      </span>
                    </div>
                    {pricing.discountTotal > 0 && (
                      <div className="flex justify-between text-sm text-green-600 dark:text-green-300">
                        <span>{t('rental.wizard.discount')}</span>
                        <span data-testid="summary-discount">
                          -{formatCurrency(pricing.discountTotal)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.wizard.vat27')}
                      </span>
                      <span className="text-gray-900 dark:text-white" data-testid="summary-vat">
                        {formatCurrency(pricing.vatAmount)}
                      </span>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  <div className="flex justify-between">
                    <span className="flex items-center font-medium text-foreground">
                      {t('rental.wizard.grossRentalFee')}
                      {pricing && <PriceBreakdownDialog pricing={pricing} />}
                    </span>
                    <span
                      className="font-medium text-gray-900 dark:text-white"
                      data-testid="summary-grand-total"
                    >
                      {formatCurrency(pricing.grandTotal)}
                    </span>
                  </div>

                  {/* Cross-sell product totals (Story Q-XSL-1) */}
                  {crossSellItems.length > 0 && (
                    <>
                      <hr className="border-gray-200 dark:border-[#2d7d8a]" />
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-[#ccfbf1]">
                            {t('rental.crossSell.productTotal')} ({t('rental.crossSell.net')})
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatCurrency(crossSellNetTotal)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-[#ccfbf1]">
                            {t('rental.crossSell.productVat')}
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatCurrency(crossSellVat)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm font-medium">
                          <span className="text-gray-900 dark:text-white">
                            {t('rental.crossSell.productTotal')} ({t('rental.crossSell.gross')})
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatCurrency(crossSellGrossTotal)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  {/* Consignment value on delivery note (P0-1) */}
                  {consignmentTotal > 0 && (
                    <>
                      <hr className="border-gray-200 dark:border-[#2d7d8a]" />
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-600 dark:text-amber-400">
                          {t('rental.consignment.deliveryNote')}
                        </span>
                        <span className="font-medium text-amber-600 dark:text-amber-400">
                          {formatHuf(consignmentTotal)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('rental.consignment.consignmentNote')}
                      </p>
                    </>
                  )}

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  {/* Kaució — only on rental, NOT on products (Q-XSL-1) */}
                  <div className="flex justify-between text-amber-600">
                    <span className="font-medium">
                      {t('rental.deposit')}
                      {crossSellItems.length > 0 && (
                        <span className="ml-1 text-xs font-normal">
                          ({t('rental.crossSell.depositNote')})
                        </span>
                      )}
                    </span>
                    <span className="font-medium" data-testid="summary-deposit">
                      {formatCurrency(pricing.depositTotal)}
                    </span>
                  </div>

                  {/* FE-E: hűségpont-beváltás csúszka (rejtve ha 0 egyenleg) */}
                  {partner && (
                    <LoyaltyRedeemSlider
                      partnerId={partner.id}
                      loyaltyPoints={partner.loyaltyPoints ?? 0}
                      rentalGrossAmount={String(pricing.grandTotal)}
                    />
                  )}

                  {/* FE-E: a beváltott hűségpont levonás-sora (post-VAT credit) */}
                  {loyaltyAmount > 0 && (
                    <div
                      className="flex justify-between text-sm"
                      data-testid="summary-loyalty-credit"
                    >
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {t('rental.loyalty.title')}
                      </span>
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">
                        −{formatCurrency(loyaltyAmount)}
                      </span>
                    </div>
                  )}

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  <div className="rounded-lg bg-primary p-4 text-white">
                    <div className="flex justify-between">
                      <span className="font-semibold">{t('rental.wizard.totalToPay')}</span>
                      <span className="text-2xl font-bold" data-testid="summary-total-to-pay">
                        {formatCurrency(totalToPay)}
                      </span>
                    </div>
                  </div>

                  {/* Deposit Payment Toggle */}
                  {(pricing?.depositTotal ?? 0) > 0 && (
                    <label
                      className="flex cursor-pointer items-center justify-between rounded-lg border dark:border-[#2d7d8a] p-4 transition-colors hover:bg-gray-50 dark:hover:bg-[#0f3d47]"
                      style={
                        depositPaid
                          ? {
                              background: 'var(--card-bg-selected)',
                              border: '2px solid hsl(var(--primary))',
                            }
                          : undefined
                      }
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {t('rental.wizard.depositPaidLabel')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                          {formatCurrency(pricing?.depositTotal ?? 0)}
                          {' — '}
                          {depositPaid
                            ? t('rental.wizard.depositPaidYes')
                            : t('rental.wizard.depositPaidNo')}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={depositPaid}
                        onChange={e => setDepositPaid(e.target.checked)}
                        className="h-5 w-5 rounded text-primary focus:ring-primary"
                      />
                    </label>
                  )}

                  {/* Payment Method Selection */}
                  <fieldset className="space-y-3" aria-required="true">
                    <legend className="font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('rental.wizard.selectPaymentMethod')}
                    </legend>
                    <div className="space-y-2">
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg border dark:border-[#2d7d8a] p-3 hover:bg-gray-50 dark:hover:bg-[#0f3d47]">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={PaymentMethod.CASH}
                          checked={paymentMethod === PaymentMethod.CASH}
                          onChange={() => setPaymentMethod(PaymentMethod.CASH)}
                          className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm font-medium">{t('rental.cashPayment')}</span>
                      </label>
                      {/* demo-trust: a CARD-tile ELREJTVE amíg MYPOS_ENABLED=false
                          (nem disabled — ne mutassunk graceful-FAILED opciót) */}
                      {isMyposEnabled() && (
                        <label className="flex cursor-pointer items-center gap-2 rounded-lg border dark:border-[#2d7d8a] p-3 hover:bg-gray-50 dark:hover:bg-[#0f3d47]">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={PaymentMethod.CARD}
                            checked={paymentMethod === PaymentMethod.CARD}
                            onChange={() => setPaymentMethod(PaymentMethod.CARD)}
                            className="h-4 w-4 text-primary"
                          />
                          <span className="text-sm font-medium">{t('rental.wizard.bankCard')}</span>
                          <span className="ml-auto rounded bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:text-blue-300">
                            MyPOS
                          </span>
                        </label>
                      )}
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg border dark:border-[#2d7d8a] p-3 hover:bg-gray-50 dark:hover:bg-[#0f3d47]">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={PaymentMethod.TRANSFER}
                          checked={paymentMethod === PaymentMethod.TRANSFER}
                          onChange={() => setPaymentMethod(PaymentMethod.TRANSFER)}
                          disabled={partner?.type !== 'COMPANY'}
                          className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm font-medium">{t('rental.transferPayment')}</span>
                        {partner?.type !== 'COMPANY' && (
                          <span className="ml-1 text-xs text-gray-400 dark:text-[#99f6e4]">
                            {t('rental.wizard.companyOnly')}
                          </span>
                        )}
                      </label>
                    </div>
                    {paymentMethod === null && (
                      <p className="text-sm text-red-500 dark:text-red-400">
                        {t('rental.wizard.selectPaymentRequired')}
                      </p>
                    )}
                    {/* FG-3-2: Céges készpénzes tranzakcióhoz adószám kötelező */}
                    {paymentMethod === PaymentMethod.CASH &&
                      partner?.type === 'COMPANY' &&
                      !partner.taxNumber && (
                        <p className="text-sm text-red-500 dark:text-red-400">
                          {t('partner.cashB2bTaxNumberRequired')}
                        </p>
                      )}
                  </fieldset>

                  {/* Disclaimer */}
                  <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                    {t('rental.wizard.disclaimer')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
